import type { ReactElement } from 'react';

import { useTranslation } from '@govard/i18n-core/react';
import { Button, Typography } from 'antd';
import { ArrowRight, Sun } from 'lucide-react';

import type {
  ManagerBriefing,
  ManagerBriefingHotLead,
  ManagerBriefingMeeting,
  ManagerBriefingPriority,
  ManagerBriefingPriorityKind,
  ManagerBriefingRiskDeal,
  ManagerBriefingTask,
} from '../../features/dashboard/api/dashboard.client.js';
import type { SalesHealthOverview } from '../../features/dashboard/api/dashboard.client.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

const { Text } = Typography;

export interface MyDayWidgetProps {
  briefing?: ManagerBriefing;
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  /** Read-only Sales Health projection; its failure never blocks other sections. */
  salesHealth?: SalesHealthOverview;
  salesHealthLoading?: boolean;
  salesHealthError?: Error | null;
  onOpenDeal?: (dealId: string) => void;
  onOpenTask?: (taskId: string) => void;
  onOpenMeeting?: (meetingId: string) => void;
  onRunPriority?: (kind: ManagerBriefingPriorityKind) => void;
  /** Retention hook: ask the AI for a touch strategy for a risk deal. */
  onProposeTouchStrategy?: (dealId: string) => void;
  className?: string;
  testId?: string;
}

const SECTION_LIMIT = 3;
const SALES_HEALTH_TOP_LIMIT = 3;
const PIPELINE_STAGES = ['New', 'Contacted', 'Negotiation', 'Won', 'Lost'] as const;

/** Spec D row hairline: 42px transcript rows with a 1px divider. */
const ROW_STYLE = {
  borderBottom: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
  transition: 'background-color 110ms ease',
} as const;

function formatMoney(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function formatTimeRange(meeting: ManagerBriefingMeeting, locale: string): string {
  const timeLocale = locale === 'en' ? 'en-US' : 'ru-RU';
  const format = (iso: string): string =>
    new Date(iso).toLocaleTimeString(timeLocale, { hour: '2-digit', minute: '2-digit' });
  return `${format(meeting.startsAt)} – ${format(meeting.endsAt)}`;
}

/** Spec D status dot: 6px semantic marker that prefixes each transcript row. */
function StatusDot({ color, pulse = false }: { color: string; pulse?: boolean }): ReactElement {
  return (
    <span
      className={pulse ? 'crm-dot-pulse' : undefined}
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

function SectionHeading({ children }: { children: string }): ReactElement {
  return (
    <div
      className="uppercase font-semibold"
      style={{
        color: 'var(--ant-color-text-tertiary, #6d6963)',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.07em',
        margin: '10px 14px 2px',
      }}
    >
      {children}
    </div>
  );
}

function RecordButton({
  title,
  subtitle,
  dotColor,
  dotPulse = false,
  onClick,
}: {
  title: string;
  subtitle?: string;
  dotColor?: string;
  dotPulse?: boolean;
  onClick?: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="transition-colors text-left border-0 flex w-full items-center gap-2 bg-transparent crm-hover-fill"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        padding: '0 14px',
        minHeight: 42,
        ...ROW_STYLE,
      }}
    >
      {dotColor ? <StatusDot color={dotColor} pulse={dotPulse} /> : null}
      <span className="min-w-0 flex-1 py-1.5">
        <Text
          strong
          className="truncate block"
          style={{ fontSize: 12.5, color: 'var(--ant-color-text)', lineHeight: 1.3 }}
        >
          {title}
        </Text>
        {subtitle ? (
          <Text type="secondary" style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)' }}>
            {subtitle}
          </Text>
        ) : null}
      </span>
    </button>
  );
}

function HotLeadRow({
  lead,
  onOpenDeal,
}: {
  lead: ManagerBriefingHotLead;
  onOpenDeal?: (dealId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const props = {
    title: lead.title,
    subtitle: t(`crm.hot_leads.reason.${lead.reason}`),
    dotColor: 'var(--ant-color-primary-border-hover, #d97706)',
    ...(onOpenDeal ? { onClick: () => onOpenDeal(lead.id) } : {}),
  };
  return <RecordButton {...props} />;
}

function RiskDealRow({
  deal,
  onOpenDeal,
  onProposeTouchStrategy,
}: {
  deal: ManagerBriefingRiskDeal;
  onOpenDeal?: (dealId: string) => void;
  onProposeTouchStrategy?: (dealId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const props = {
    title: deal.title,
    subtitle: t(`crm.hot_leads.reason.${deal.reason}`),
    dotColor: 'var(--ant-color-error, #b91c1c)',
    dotPulse: true,
    ...(onOpenDeal ? { onClick: () => onOpenDeal(deal.id) } : {}),
  };
  if (onProposeTouchStrategy === undefined) {
    return <RecordButton {...props} />;
  }
  // The strategy action renders as a separate sibling button (never nested
  // inside the row's own <button>): the row keeps its full-width hit area
  // while the compact action stays reachable on narrow screens.
  return (
    <div className="flex w-full items-stretch">
      <div className="min-w-0 flex-1">
        <RecordButton {...props} />
      </div>
      <button
        type="button"
        data-testid={`my-day-risk-deal-strategy-${deal.id}`}
        title={deal.title}
        onClick={() => onProposeTouchStrategy(deal.id)}
        className="flex shrink-0 cursor-pointer items-center justify-center border-0 border-l bg-transparent px-3 text-[11px] font-medium text-[var(--ant-color-text)] transition-colors hover:bg-[var(--ant-color-primary-bg)] hover:text-[var(--ant-color-primary-text)] focus-visible:outline-2 focus-visible:outline-[var(--ant-color-primary-border-hover)]"
        style={{
          borderBottom: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
          borderLeft: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
        }}
      >
        {t('crm.dashboard.my_day.propose_strategy')}
      </button>
    </div>
  );
}

function OverdueTaskRow({
  task,
  onOpenTask,
}: {
  task: ManagerBriefingTask;
  onOpenTask?: (taskId: string) => void;
}): ReactElement {
  const { t, locale } = useTranslation();
  const dueLabel = task.dueAt
    ? new Date(task.dueAt).toLocaleString(locale === 'en' ? 'en-US' : 'ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  const context = [task.dealTitle, task.contactName, task.companyName].find(Boolean);
  const props = {
    title: task.title,
    subtitle:
      [dueLabel, context].filter(Boolean).join(' · ') || t('crm.dashboard.open_tasks.no_due'),
    dotColor: 'var(--ant-color-warning, #c2410c)',
    ...(onOpenTask ? { onClick: () => onOpenTask(task.id) } : {}),
  };
  return <RecordButton {...props} />;
}

function MeetingRow({
  meeting,
  onOpenMeeting,
}: {
  meeting: ManagerBriefingMeeting;
  onOpenMeeting?: (meetingId: string) => void;
}): ReactElement {
  const { t, locale } = useTranslation();
  const context = [meeting.dealTitle, meeting.contactName].find(Boolean);
  const props = {
    title: meeting.title,
    subtitle:
      [formatTimeRange(meeting, locale), context].filter(Boolean).join(' · ') ||
      t('crm.dashboard.open_tasks.no_due'),
    dotColor: 'var(--ant-color-info, #2563eb)',
    ...(onOpenMeeting ? { onClick: () => onOpenMeeting(meeting.id) } : {}),
  };
  return <RecordButton {...props} />;
}

function PriorityChips({
  priorities,
  briefing,
  onRunPriority,
}: {
  priorities: readonly ManagerBriefingPriority[];
  briefing: ManagerBriefing;
  onRunPriority?: (kind: ManagerBriefingPriorityKind) => void;
}): ReactElement {
  const { t } = useTranslation();
  const recordCounts: Record<ManagerBriefingPriorityKind, number> = {
    meetings: briefing.todayMeetings.length,
    overdue_tasks: briefing.overdueTasks.length,
    risk_deals: briefing.riskDeals.length,
    hot_leads: briefing.hotLeads.length,
  };
  return (
    <div className="flex flex-wrap items-center" style={{ gap: 6, padding: '6px 14px 2px' }}>
      {priorities.map((priority) => {
        const label = t(`crm.dashboard.my_day.priority.${priority.kind}`);
        const count = typeof priority.count === 'number' ? priority.count : 0;
        const hasRecords = recordCounts[priority.kind] > 0;
        const actionLabel = hasRecords ? `${label} · ${count}` : label;
        const ariaLabel = hasRecords
          ? t('crm.dashboard.my_day.run_ai', { label: actionLabel })
          : t('crm.dashboard.my_day.no_priority_records');
        return (
          <button
            key={priority.kind}
            type="button"
            data-testid={`my-day-priority-${priority.kind}`}
            aria-label={ariaLabel}
            title={hasRecords ? ariaLabel : t('crm.dashboard.my_day.no_priority_records')}
            disabled={!hasRecords || onRunPriority === undefined}
            onClick={
              onRunPriority === undefined
                ? undefined
                : () => {
                    onRunPriority(priority.kind);
                  }
            }
            className="group inline-flex min-w-0 max-w-full cursor-pointer items-center gap-1.5 crm-radius-s border border-[var(--ant-color-border)] bg-[var(--ant-color-bg-container)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--ant-color-text)] hover:-translate-y-px hover:border-[var(--ant-color-primary-border-hover)] hover:bg-[var(--ant-color-primary-bg)] hover:text-[var(--ant-color-primary-text)] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--ant-color-primary-border-hover)] disabled:hover:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              transition:
                'background-color 110ms ease, border-color 110ms ease, color 110ms ease, transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <span className="min-w-0 truncate">{label}</span>
            {typeof priority.count === 'number' ? (
              <span className="shrink-0 rounded bg-[var(--g-fill)] px-1 font-mono text-[10.5px] leading-4 text-[var(--ant-color-text-secondary)]">
                {priority.count}
              </span>
            ) : null}
            <ArrowRight
              size={12}
              className="shrink-0 text-[var(--ant-color-text-secondary)] transition-transform group-hover:translate-x-0.5"
            />
          </button>
        );
      })}
    </div>
  );
}

function SalesHealthRow({
  deal,
  onOpenDeal,
}: {
  deal: SalesHealthOverview['topDeals'][number];
  onOpenDeal?: (dealId: string) => void;
}): ReactElement {
  const { t, locale } = useTranslation();
  return (
    <button
      type="button"
      data-testid={`my-day-sales-health-deal-${deal.id}`}
      onClick={onOpenDeal === undefined ? undefined : () => onOpenDeal(deal.id)}
      disabled={onOpenDeal === undefined}
      className="transition-colors text-left border-0 flex w-full flex-col bg-transparent min-w-0 crm-hover-fill"
      style={{
        cursor: onOpenDeal ? 'pointer' : 'default',
        padding: '6px 14px',
        ...ROW_STYLE,
      }}
    >
      <span className="flex w-full min-w-0 items-baseline justify-between gap-2">
        <Text
          strong
          className="truncate block min-w-0"
          style={{ fontSize: 12.5, color: 'var(--ant-color-text)' }}
        >
          {deal.title}
        </Text>
        <Text
          type="secondary"
          style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)', flexShrink: 0 }}
          data-testid={`my-day-sales-health-probability-${deal.id}`}
        >
          {deal.closeProbability}%
        </Text>
      </span>
      <span className="flex w-full min-w-0 flex-wrap items-center gap-x-2 text-[11px]">
        <span
          className="truncate"
          style={{
            color:
              deal.healthLevel === 'critical' ? 'var(--color-error)' : 'var(--ant-color-warning)',
          }}
          data-testid={`my-day-sales-health-level-${deal.id}`}
        >
          {t(`crm.dashboard.my_day.sales_health.level_${deal.healthLevel}`)}
        </span>
        <span
          className="font-mono"
          style={{ color: 'var(--ant-color-text-tertiary)', flexShrink: 0 }}
        >
          {formatMoney(deal.amount, deal.currency, locale)}
        </span>
      </span>
    </button>
  );
}

/**
 * Compact Sales Health section. Fully isolated: loading/error/empty are handled
 * inside this component so the rest of My day always renders.
 */
function SalesHealthSection({
  salesHealth,
  salesHealthLoading,
  salesHealthError,
  onOpenDeal,
}: {
  salesHealth?: SalesHealthOverview;
  salesHealthLoading: boolean;
  salesHealthError?: Error | null;
  onOpenDeal?: (dealId: string) => void;
}): ReactElement {
  const { t, locale } = useTranslation();

  return (
    <div
      data-testid="my-day-sales-health"
      className="min-w-0"
      style={{ borderBottom: '1px solid var(--ant-color-border-secondary, #e7e5e4)' }}
    >
      <SectionHeading>{t('crm.dashboard.my_day.sales_health.title')}</SectionHeading>
      {salesHealthLoading ? (
        <div className="animate-pulse" data-testid="my-day-sales-health-loading">
          <div
            className="crm-radius-s mx-3.5 mb-2"
            style={{ height: 40, background: 'var(--g-fill, #f5f1eb)' }}
          />
        </div>
      ) : salesHealthError !== null && salesHealthError !== undefined ? (
        <p
          data-testid="my-day-sales-health-error"
          style={{
            padding: '0 14px 10px',
            fontSize: 11.5,
            color: 'var(--ant-color-text-tertiary)',
          }}
        >
          {t('crm.dashboard.my_day.sales_health.error')}
        </p>
      ) : salesHealth === undefined || salesHealth.activeDealCount === 0 ? (
        <p
          data-testid="my-day-sales-health-empty"
          style={{
            padding: '0 14px 10px',
            fontSize: 11.5,
            color: 'var(--ant-color-text-tertiary)',
          }}
        >
          {t('crm.dashboard.my_day.sales_health.empty')}
        </p>
      ) : (
        <div className="min-w-0">
          <div
            data-testid="my-day-sales-health-stats"
            className="flex flex-wrap items-center gap-x-3 gap-y-1"
            style={{ padding: '2px 14px 6px' }}
          >
            <span className="text-[12px] font-medium text-[var(--ant-color-text)]">
              {t('crm.dashboard.my_day.sales_health.active')}:{' '}
              <b className="font-mono">{salesHealth.activeDealCount}</b>
            </span>
            <span className="text-[12px] text-[var(--ant-color-text)]">
              {t('crm.dashboard.my_day.sales_health.attention')}:{' '}
              <b className="font-mono">{salesHealth.attentionCount}</b>
            </span>
            <span className="text-[12px]" style={{ color: 'var(--color-error)' }}>
              {t('crm.dashboard.my_day.sales_health.critical')}:{' '}
              <b className="font-mono">{salesHealth.criticalCount}</b>
            </span>
          </div>
          <ul data-testid="my-day-sales-health-money" style={{ padding: '0 14px 6px' }}>
            {salesHealth.totalsByCurrency.map((totals) => (
              <li
                key={totals.currency}
                className="text-[11.5px] text-[var(--ant-color-text-secondary)]"
              >
                {formatMoney(totals.pipelineAmount, totals.currency, locale)}
                {' → '}
                <span style={{ color: 'var(--ant-color-primary-text)' }}>
                  {formatMoney(totals.expectedRevenue, totals.currency, locale)}
                </span>
              </li>
            ))}
          </ul>
          {salesHealth.topDeals.length > 0 ? (
            <ul className="min-w-0" data-testid="my-day-sales-health-deals">
              {salesHealth.topDeals.slice(0, SALES_HEALTH_TOP_LIMIT).map((deal) => (
                <li key={deal.id} className="min-w-0">
                  <SalesHealthRow deal={deal} {...(onOpenDeal ? { onOpenDeal } : {})} />
                </li>
              ))}
            </ul>
          ) : (
            <p
              data-testid="my-day-sales-health-no-risky"
              style={{ padding: '0 14px 8px', fontSize: 11.5, color: 'var(--ant-color-success)' }}
            >
              {t('crm.dashboard.my_day.sales_health.all_healthy')}
            </p>
          )}
          <p
            style={{
              padding: '6px 14px 10px',
              fontSize: 10.5,
              color: 'var(--ant-color-text-tertiary)',
            }}
          >
            {t('crm.dashboard.my_day.sales_health.disclaimer')}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * «Мой день»: приоритеты и сводки дня. Фид на 42px-строках с хайрлайнами
 * (Warm Precision D), данные и обработчики не менялись.
 */
export function MyDayWidget({
  briefing,
  loading = false,
  error = null,
  onRetry,
  salesHealth,
  salesHealthLoading = false,
  salesHealthError = null,
  onOpenDeal,
  onOpenTask,
  onOpenMeeting,
  onRunPriority,
  onProposeTouchStrategy,
  className,
  testId,
}: MyDayWidgetProps): ReactElement {
  const { t } = useTranslation();
  const widgetContainerProps = {
    title: t('crm.dashboard.my_day.title'),
    icon: Sun,
    iconColor: 'var(--ant-color-primary-border-hover, #d97706)',
    minHeight: 320,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  const meetings = (briefing?.todayMeetings ?? []).slice(0, SECTION_LIMIT);
  const hotLeads = (briefing?.hotLeads ?? []).slice(0, SECTION_LIMIT);
  const overdueTasks = (briefing?.overdueTasks ?? []).slice(0, SECTION_LIMIT);
  const riskDeals = (briefing?.riskDeals ?? []).slice(0, SECTION_LIMIT);
  // The global "all quiet" state requires BOTH sources to be quiet: the
  // briefing has no records AND Sales Health is loaded as a healthy/empty
  // pipeline. Loading, errors or risky deals must suppress it.
  const salesHealthHasSignal =
    salesHealth !== undefined &&
    (salesHealth.attentionCount > 0 ||
      salesHealth.criticalCount > 0 ||
      salesHealth.topDeals.length > 0);
  const hasContent =
    meetings.length > 0 ||
    hotLeads.length > 0 ||
    overdueTasks.length > 0 ||
    riskDeals.length > 0 ||
    salesHealthLoading === true ||
    salesHealthError !== null ||
    salesHealthHasSignal;
  const priorities = briefing?.priorities ?? [];

  const contentProps = {
    ...(briefing !== undefined ? { briefing } : {}),
    ...(error ? { error } : {}),
  } satisfies Partial<MyDayWidgetProps>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse flex flex-col" style={{ gap: 6, padding: '8px 14px' }}>
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="crm-radius-s"
              style={{ height: 42, background: 'var(--g-fill, #f5f1eb)' }}
            />
          ))}
        </div>
      ) : 'error' in contentProps ? (
        <div
          className="flex flex-col items-center justify-center h-full"
          style={{ padding: 24, gap: 12 }}
        >
          <Text type="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
            {t('crm.dashboard.my_day.error')}
          </Text>
          <Button size="small" onClick={onRetry} data-testid="my-day-retry">
            {t('crm.dashboard.my_day.retry')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col" data-testid="my-day-content">
          {priorities.length > 0 && briefing !== undefined ? (
            <PriorityChips
              priorities={priorities}
              briefing={briefing}
              {...(onRunPriority ? { onRunPriority } : {})}
            />
          ) : null}
          {meetings.length > 0 ? (
            <div data-testid="my-day-meetings">
              <SectionHeading>{t('crm.dashboard.my_day.meetings')}</SectionHeading>
              {meetings.map((meeting) => (
                <MeetingRow
                  key={meeting.id}
                  meeting={meeting}
                  {...(onOpenMeeting ? { onOpenMeeting } : {})}
                />
              ))}
            </div>
          ) : null}
          {hotLeads.length > 0 ? (
            <div data-testid="my-day-hot-leads">
              <SectionHeading>{t('crm.dashboard.my_day.hot_leads')}</SectionHeading>
              {hotLeads.map((lead) => (
                <HotLeadRow key={lead.id} lead={lead} {...(onOpenDeal ? { onOpenDeal } : {})} />
              ))}
            </div>
          ) : null}
          {overdueTasks.length > 0 ? (
            <div data-testid="my-day-overdue-tasks">
              <SectionHeading>{t('crm.dashboard.my_day.overdue_tasks')}</SectionHeading>
              {overdueTasks.map((task) => (
                <OverdueTaskRow key={task.id} task={task} {...(onOpenTask ? { onOpenTask } : {})} />
              ))}
            </div>
          ) : null}
          {riskDeals.length > 0 ? (
            <div data-testid="my-day-risk-deals">
              <SectionHeading>{t('crm.dashboard.my_day.risk_deals')}</SectionHeading>
              {riskDeals.map((deal) => (
                <RiskDealRow
                  key={deal.id}
                  deal={deal}
                  {...(onOpenDeal ? { onOpenDeal } : {})}
                  {...(onProposeTouchStrategy ? { onProposeTouchStrategy } : {})}
                />
              ))}
            </div>
          ) : null}
          <SalesHealthSection
            {...(salesHealth !== undefined ? { salesHealth } : {})}
            salesHealthLoading={salesHealthLoading}
            salesHealthError={salesHealthError}
            {...(onOpenDeal ? { onOpenDeal } : {})}
          />
          {!hasContent ? (
            <div
              className="flex items-center justify-center"
              data-testid="my-day-empty"
              style={{ padding: '14px 16px', opacity: 0.6 }}
            >
              <Sun
                size={18}
                style={{ color: 'var(--ant-color-success, #15803d)', marginRight: 8 }}
              />
              <Text
                type="secondary"
                style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}
              >
                {t('crm.dashboard.my_day.empty')}
              </Text>
            </div>
          ) : null}
          {briefing?.pipeline ? (
            <div data-testid="my-day-pipeline" style={{ padding: '12px 14px 14px' }}>
              <SectionHeading>{t('crm.dashboard.my_day.pipeline')}</SectionHeading>
              <div
                className="grid grid-cols-3 gap-px overflow-hidden crm-radius-s sm:grid-cols-6"
                style={{ background: 'var(--ant-color-border-secondary, #e7e5e4)', marginTop: 4 }}
              >
                {PIPELINE_STAGES.map((stage) => (
                  <div
                    key={stage}
                    style={{
                      background: 'var(--ant-color-bg-container, #fffdfa)',
                      padding: '8px 10px',
                      minWidth: 0,
                    }}
                  >
                    <Text
                      type="secondary"
                      className="block truncate uppercase"
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        color: 'var(--ant-color-text-tertiary)',
                      }}
                    >
                      {t(`crm.deals.stage.${stage}`)}
                    </Text>
                    <Text
                      strong
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: 15,
                        color: 'var(--ant-color-text)',
                      }}
                    >
                      {briefing.pipeline[stage.toLowerCase() as 'new']}
                    </Text>
                  </div>
                ))}
                <div
                  style={{
                    background: 'var(--ant-color-bg-container, #fffdfa)',
                    padding: '8px 10px',
                    minWidth: 0,
                  }}
                >
                  <Text
                    type="secondary"
                    className="block truncate uppercase"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      color: 'var(--ant-color-text-tertiary)',
                    }}
                  >
                    {t('crm.dashboard.my_day.conversion')}
                  </Text>
                  <Text
                    strong
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 15,
                      color: 'var(--ant-color-success, #15803d)',
                    }}
                  >
                    {briefing.pipeline.conversionRate}%
                  </Text>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </WidgetContainer>
  );
}
